export * from './const'
export * from './entity'
export * from './helper'
export * from './types'

import _ from 'lodash'
import type { CanvasEngineConstructor } from './types'
import type { RendererKind } from './backend/types'
import { CanvasEngineHandleController } from './handleController'
import { CANVAS_HANDLE_MODE } from './const'

export class CanvasEngine extends CanvasEngineHandleController {
  /**
   * 렌더러 종류. 현재는 'konva'만 지원한다(생성자의 createRenderer가 판정).
   */
  public readonly rendererKind: RendererKind

  public static async create(arg: CanvasEngineConstructor): Promise<CanvasEngine> {
    const canvas = new CanvasEngine(arg)
    return canvas
  }

  constructor(arg: CanvasEngineConstructor) {
    super(arg)
    this.rendererKind = this.renderer.kind

    // 기본 모드로 설정
    this.changeCanvasHandleMode(CANVAS_HANDLE_MODE.DEFAULT)
  }

  /**
   * 현재 배경 전체를 jpeg로 출력한다.
   * @param options.hideBrush true면 브러시 엔티티를 투명 처리하고 출력(출력 후 원복). 경계 API — 바깥에서 opacity()를 만지지 않도록.
   */
  toJpegFile = async <T extends 'base64' | 'blob'>(
    type: T = 'base64' as T,
    options: { hideBrush?: boolean } = {},
  ): Promise<T extends 'base64' ? string : Blob> => {
    const hiddenBrushes = options.hideBrush ? this.entities.filter(entity => entity.isBrush()) : []
    hiddenBrushes.forEach(entity => entity.node.opacity(0))
    try {
      return await this.captureJpeg(type)
    } finally {
      hiddenBrushes.forEach(entity => entity.node.opacity(1))
    }
  }

  private captureJpeg = async <T extends 'base64' | 'blob'>(
    type: T,
  ): Promise<T extends 'base64' ? string : Blob> => {
    // 휠 줌 직후 호출되면 브러시가 임시 스냅샷 상태일 수 있으므로 제스처를 먼저 종료해 실제 도형으로 출력
    this.flushWheelGesture()
    // 이는 렌더링 된 화면 기준으로 캡쳐하는 방법 (뷰포트 1:1 리셋·흰 배경·원복은 renderer.exportImage)
    const captureWidth =
      this.backgroundRotation == 90 || this.backgroundRotation == 270
        ? this.backgroundHeight
        : this.backgroundWidth
    const captureHeight =
      this.backgroundRotation == 90 || this.backgroundRotation == 270
        ? this.backgroundWidth
        : this.backgroundHeight

    this.blurAllEntity()
    const x = captureWidth * 0.05 * -1
    const y = captureHeight * 0.05 * -1
    const width = captureWidth + captureWidth * 0.05 * 2
    const height = captureHeight + captureHeight * 0.05 * 2
    const region = {
      x,
      y,
      width,
      height,
      mimeType: 'image/jpeg',
      quality: 1,
      backdropFill: 'white',
    }

    if (type == 'base64') {
      const image = await this.renderer.exportImage({ ...region, type: 'base64' })
      return image as T extends 'base64' ? string : Blob
    } else if (type == 'blob') {
      const image = await this.renderer.exportImage({ ...region, type: 'blob' })
      return image as T extends 'base64' ? string : Blob
    }
    throw new Error('지원하지 않는 타입입니다.')
  }
}
